import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAboutUsContent } from '../utils/api';

const About = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default static content (fallback)
  const defaultContent = {
    who_we_are: '',
    our_focus: '',
    our_logo_description: '',
    our_logo_url: ''
  };

  useEffect(() => {
    const loadContent = async () => {
      try {
        const res = await getAboutUsContent();
        const data = res.data || {};
        // Only use CMS content if it has actual data
        if (data.who_we_are || data.our_focus) {
          setContent(data);
        } else {
          setContent(null); // Use static content
        }
      } catch (error) {
        console.error('Failed to load About Us content:', error);
        setContent(null);
      } finally {
        setLoading(false);
      }
    };
    
    loadContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1818]"></div>
      </div>
    );
  }

  // If CMS content exists, render it
  if (content && (content.who_we_are || content.our_focus)) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        {/* Header */}
        <div className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              About Us
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              At <span className="text-[#8B1818] font-semibold">Lex</span>DoRight, we make it effortless to know the law, use powerful tools, and do what is right with precision.
            </p>
          </div>
        </div>

        {/* Dynamic Content */}
        {content.who_we_are && (
          <section className="py-16 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
                Who We Are
              </h2>
              <div className="prose prose-lg max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: content.who_we_are }} />
            </div>
          </section>
        )}

        {content.our_focus && (
          <section className="py-16 bg-slate-50">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
                Our Focus
              </h2>
              <div className="prose prose-lg max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: content.our_focus }} />
            </div>
          </section>
        )}

        {(content.our_logo_description || content.our_logo_url) && (
          <section className="py-16 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
                Our Logo
              </h2>
              {content.our_logo_url && (
                <div className="flex justify-center mb-8">
                  <img src={content.our_logo_url} alt="LexDoRight Logo" className="h-40 object-contain" />
                </div>
              )}
              {content.our_logo_description && (
                <div className="prose prose-lg max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: content.our_logo_description }} />
              )}
            </div>
          </section>
        )}
      </div>
    );
  }

  // Default static content (original page)
  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            Our Focus
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            At <span className="text-[#8B1818] font-semibold">Lex</span>DoRight, we make it effortless to know the law, use powerful tools, and do what is right with precision.
          </p>
        </div>
      </div>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              At <span className="text-[#8B1818] font-semibold">Lex</span>DoRight, the objective is simple: make it easier for leaders, institutions, and professionals to do what is right, consistently, confidently, and at scale. <span className="text-[#8B1818] font-semibold">Lex</span>DoRight sits at the intersection of law, governance, and technology, turning dense rules and complex procedures into clear, workable tools that fit how people actually plan, decide, and get things done.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
            Who We Serve
          </h2>
          <div className="space-y-8">
            <p className="text-lg text-slate-700 leading-relaxed">
              <span className="text-[#8B1818] font-semibold">Lex</span>DoRight exists for people who live with legal and ethical decisions every day, whether in a courtroom, a boardroom, a government office, a classroom, or a startup.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Law Professionals */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Law Professionals</h3>
                <p className="text-slate-600">
                  Gain a focused, structured workspace for research, drafting, and review, helping them move from complex questions to clear, defensible positions with less friction.
                </p>
              </div>

              {/* Entrepreneurs & Business Owners */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Entrepreneurs & Business Owners</h3>
                <p className="text-slate-600">
                  Get practical support that turns regulatory language into concrete steps for building compliant, well governed organizations.
                </p>
              </div>

              {/* Government Officials */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Government Officials & Public Servants</h3>
                <p className="text-slate-600">
                  Lean on <span className="text-[#8B1818] font-semibold">Lex</span>DoRight to anchor transparent, rules based decisions that stand up to scrutiny, investigation, and audit.
                </p>
              </div>

              {/* Researchers & Students */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Researchers & Students</h3>
                <p className="text-slate-600">
                  Benefit from organized references and intuitive workflows that make intricate legal and governance concepts easier to explore, teach, learn, and apply in the real world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beyond Software Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
            Beyond Software
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-slate-700 leading-relaxed">
              Beyond software, <span className="text-[#8B1818] font-semibold">Lex</span>DoRight also comes to life through thoughtfully designed physical and digital items that support everyday practice. Governance themed souvenirs, desk accessories, event kits, eBooks, and printed references keep key legal and governance concepts visible in offices, trainings, and stakeholder engagements, reinforcing a culture where doing what is right becomes a daily habit rather than an occasional ideal.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
